import type {
    ComponentPropsWithRef,
    ComponentPropsWithoutRef,
    ReactNode,
    ReactElement
} from 'react';
import { createContext, forwardRef, useContext } from 'react';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

import Icon from '@/components/base/Icon';

export const VariantsType = {
    error: {
        backgroundColor: 'bg-icoRed-100 dark:bg-icoRed/20',
        icon: 'disconnect',
        textColor: 'text-icoRed',
        borderColor: 'border-icoRed-200 dark:border-icoRed'
    },
    success: {
        backgroundColor: 'bg-icoGreen-100 dark:bg-icoGreen/20',
        icon: 'check',
        textColor: 'text-icoGreen',
        borderColor: 'border-icoGreen-200 dark:border-icoGreen'
    },
    warning: {
        backgroundColor: 'bg-icoOrange-100 dark:bg-icoOrange/20',
        icon: 'disconnect',
        textColor: 'text-icoOrange',
        borderColor: 'border-icoOrange-200 dark:border-icoOrange'
    },
    info: {
        backgroundColor: 'bg-icoBlue-100 dark:bg-icoBlue/20',
        textColor: 'text-icoBlue',
        borderColor: 'border-icoBlue-200 dark:border-icoBlue'
    },
    update: {
        backgroundColor: 'bg-icoAquaBlue-100 dark:bg-icoAquaBlue/20',
        textColor: 'text-icoAquaBlue',
        borderColor: 'border-icoAquaBlue-200 dark:border-icoAquaBlue'
    },
    lockedFeature: {
        backgroundColor: 'bg-icoBlue-100',
        icon: 'crown',
        borderColor: 'border-icoAquaBlue-200 dark:border-icoAquaBlue',
        textColor: 'text-icoBlue'
    }
};

const variantsSize = {
    xxs: 'px-3 py-3 font-GMedium text-xxs space-x-3',
    xs: 'px-5 py-3 font-GMedium text-xs space-x-5'
};

const CompoundBannerContext = createContext<{ type: keyof typeof VariantsType }>(null);

export type CompoundBannerProps = ComponentPropsWithRef<'div'> & {
    type?: keyof typeof VariantsType;
    size?: keyof typeof variantsSize;
    noBorder?: boolean;
};

export const CompoundBanner = forwardRef<HTMLDivElement, CompoundBannerProps>(
    ({ type = 'error', size = 'xs', noBorder = false, className, children, ...restProps }, ref) => {
        return (
            <CompoundBannerContext.Provider value={{ type }}>
                <div
                    data-testid={`banner-${type}`}
                    ref={ref}
                    className={clsx(
                        'flex w-full items-center rounded-md',
                        VariantsType[type]?.backgroundColor,
                        VariantsType[type]?.textColor,
                        variantsSize[size],
                        className,
                        {
                            [VariantsType[type]?.borderColor]: !noBorder,
                            border: !noBorder
                        }
                    )}
                    {...restProps}>
                    {children}
                </div>
            </CompoundBannerContext.Provider>
        );
    }
);

function BannerIconSection({ icon, children }: { icon?: string; children?: ReactNode }) {
    const { type } = useContext(CompoundBannerContext);

    if (children) {
        return children;
    }

    if (icon) {
        return <Icon icon={icon} className="text-xl" data-testid="banner-forced-icon" />;
    }

    if (!icon && VariantsType[type]?.['icon']) {
        return (
            <Icon
                icon={VariantsType[type]?.['icon']}
                data-testid={`banner-${type}-icon`}
                className="text-xl"
            />
        );
    }

    return null;
}

type ContentSectionProps = Omit<ComponentPropsWithoutRef<'div'>, 'title'> & {
    title?: string | ReactElement;
    message?: string | ReactElement;
};
function ContentSection({ title, className, message, children }: ContentSectionProps) {
    const { type } = useContext(CompoundBannerContext);

    return (
        <div className={clsx('flex-1 space-y-1', VariantsType[type]['textColor'], className)}>
            {title ? (
                <p className="font-GBold" data-testid={`banner-${type}-title`}>
                    {title}
                </p>
            ) : null}
            {message ? <p data-testid={`banner-${type}-message`}>{message}</p> : null}
            {children}
        </div>
    );
}

function Close({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation('commons');

    return (
        <button
            aria-label={t('close')}
            data-testid="button-banner-close"
            type="button"
            className="relative h-4 w-4 overflow-hidden rounded-sm text-base focus:outline-none focus:ring-2"
            onClick={onClose}>
            <Icon icon="close" />
            <p className="absolute text-transparent">{t('close')}</p>
        </button>
    );
}

const InfoBanner = Object.assign({}, CompoundBanner, {
    Icon: BannerIconSection,
    Content: ContentSection,
    Close: Close
});

export default InfoBanner;

CompoundBanner.displayName = 'CompoundBanner';
